/**
 * One column order for every site-visit table.
 *
 * The Planning Visits Board, Implementation Visits Board, the reporting boards
 * and lists, and the Completed tables all show overlapping slices of the same
 * site visit card, but each had grown its own order: the reporting lists opened
 * with Approval Status, Field Team Status put Location before Field Report
 * Status, Completed put IP before Location, and two tables shipped the same
 * column twice. Moving between them meant re-reading the header row every time.
 *
 * The sequence below is the single order they all follow now. A table only shows
 * the columns it has data for — it never has to think about where they go, and a
 * column keeps the same position from Planning through Completed.
 *
 * `aliases` exist because the tables key the same column differently
 * ("siteVisitIdentifier" / "identifier" / "siteVisitCardIdentifier"). Rather
 * than rename keys across ten files — keys drive antd's own bookkeeping — each
 * spelling maps onto one canonical position.
 *
 * `phase` is only used to label what a table is hiding; visibility itself is
 * driven by each table's own default set, so a board can keep a later-phase
 * column on screen when it needs one (the Implementation board shows Field
 * Report Status, for instance).
 */

export const COLUMN_PHASES = {
  identity: "Identity",
  planning: "Planning",
  implementation: "Implementation",
  reporting: "Reporting",
};

const CANONICAL_COLUMNS = [
  // Identity — always on screen, in this order, in every table.
  { key: "identifier", label: "Site Visit Identifier", phase: "identity", aliases: ["siteVisitIdentifier", "siteVisitCardIdentifier"] },
  { key: "name", label: "Site Visit Name", phase: "identity", aliases: ["siteVisitName", "siteName"] },
  { key: "siteVisitStatus", label: "Site Visit Status", phase: "identity", aliases: ["status"] },

  // Planning — what was agreed before anyone travelled.
  { key: "dateOfPlannedVisit", label: "Date of Target Visit", phase: "planning", aliases: ["plannedVisit", "dateOfTargetVisit", "targetDate"] },
  { key: "facility", label: "Facility", phase: "planning", aliases: [] },
  { key: "ip", label: "IP", phase: "planning", aliases: ["implementingPartner", "ipPlan"] },
  { key: "location", label: "Location", phase: "planning", aliases: ["country"] },
  { key: "state", label: "Governate/State", phase: "planning", aliases: ["governate", "stateGovernate"] },
  { key: "cluster", label: "Cluster", phase: "planning", aliases: [] },
  { key: "virtual", label: "Virtual", phase: "planning", aliases: ["isVirtual", "vitrual"] },
  { key: "tpmCoordinator", label: "TPM Coordinator", phase: "planning", aliases: ["coordinator"] },
  { key: "team", label: "Team", phase: "planning", aliases: ["teamName"] },
  // Planning fields only the Site Visit Summary edits, kept in the sequence so
  // that table follows the same order as the boards rather than its own.
  { key: "isTeamLocal", label: "Local Team", phase: "planning", aliases: [] },
  { key: "coordinationType", label: "Coordination Type", phase: "planning", aliases: [] },
  { key: "activationDate", label: "Activation Date", phase: "planning", aliases: [] },
  { key: "visitPlanApproval", label: "Visit Plan Approval", phase: "planning", aliases: [] },
  { key: "beneficiariesTarget", label: "Beneficiary Surveys", phase: "planning", aliases: [] },
  { key: "kiiTarget", label: "KII Contacts", phase: "planning", aliases: [] },
  { key: "observationTarget", label: "Observation Checklists", phase: "planning", aliases: [] },
  { key: "mediaLimit", label: "Media Limit", phase: "planning", aliases: [] },
  { key: "samplingInstructions", label: "Sampling Instructions", phase: "planning", aliases: [] },
  { key: "clientInstructions", label: "Client Instructions", phase: "planning", aliases: [] },
  { key: "otherInstructions", label: "Other Instructions", phase: "planning", aliases: [] },
  { key: "specificsOfOnSiteOperation", label: "On-site Operation Specifics", phase: "planning", aliases: [] },
  { key: "fulcrumAccount", label: "Data Collection Account", phase: "planning", aliases: [] },

  // Implementation — what happened in the field.
  { key: "reportingMonthYear", label: "Reporting Month-Year", phase: "implementation", aliases: ["reportingMonth", "reportingPeriod"] },
  { key: "reportingYear", label: "Reporting Year", phase: "implementation", aliases: [] },
  { key: "trainingIsCompleted", label: "Training Completed", phase: "implementation", aliases: ["trainingCompleted"] },
  { key: "trainingDate", label: "Training Date", phase: "implementation", aliases: [] },
  { key: "trainingScoreInfo", label: "Training Score Info", phase: "implementation", aliases: [] },
  { key: "actualDateOfVisit", label: "Actual Date of Visit", phase: "implementation", aliases: ["actualDatesOfVisit", "siteTrackingVisitDates"] },
  { key: "percentageCompleted", label: "% Data Collection Completed", phase: "implementation", aliases: ["percentageOfDataCollectionCompleted"] },
  { key: "isPostponed", label: "Postponed", phase: "implementation", aliases: ["postponed"] },
  { key: "postponedReason", label: "Postponed Reason", phase: "implementation", aliases: [] },
  { key: "postponedDetails", label: "Postponed Details", phase: "implementation", aliases: [] },
  { key: "notes", label: "Notes", phase: "implementation", aliases: [] },

  // Reporting — what came out of it.
  { key: "fieldTeamReportingStatus", label: "Field Report Status", phase: "reporting", aliases: ["fieldReportStatus"] },
  { key: "approvalStatus", label: "Approval Status", phase: "reporting", aliases: ["rejected"] },
  { key: "dateOfReport", label: "Date of Report", phase: "reporting", aliases: [] },
  { key: "dateOfDebrief", label: "Date of Debrief", phase: "reporting", aliases: [] },
  { key: "linkToDataCollectionResults", label: "Link to Data Collection Results", phase: "reporting", aliases: [] },
  { key: "hasPhotos", label: "Photos Required", phase: "reporting", aliases: [] },
  { key: "photoCount", label: "Photos", phase: "reporting", aliases: [] },
  { key: "hasVideos", label: "Videos Required", phase: "reporting", aliases: [] },
  { key: "videoCount", label: "Videos", phase: "reporting", aliases: [] },
  { key: "resources", label: "Resources", phase: "reporting", aliases: [] },
  { key: "dataChecks", label: "Data Checks", phase: "reporting", aliases: [] },
  { key: "datasetList", label: "Dataset List", phase: "reporting", aliases: ["dataset"] },
];

/**
 * What each phase's tables open with, beyond the three identity columns that are
 * always on. Shared constants rather than per-file lists so every table in a
 * phase agrees — the reporting boards should not each pick their own five.
 *
 * A key listed here that a given table has no column for is simply ignored, so
 * one set can cover tables with slightly different data.
 */
export const IMPLEMENTATION_DEFAULT_COLUMNS = [
  "facility",
  "dateOfPlannedVisit",
  "actualDateOfVisit",
  "reportingMonthYear",
  "trainingIsCompleted",
  "fieldTeamReportingStatus",
];

export const REPORTING_DEFAULT_COLUMNS = [
  "facility",
  "actualDateOfVisit",
  "fieldTeamReportingStatus",
  "approvalStatus",
  "dateOfReport",
  "resources",
];

export const COMPLETED_DEFAULT_COLUMNS = [
  "facility",
  "actualDateOfVisit",
  "fieldTeamReportingStatus",
  "dateOfReport",
  "resources",
  "datasetList",
];

// Row-action columns close every table; anything unrecognised sits between the
// canonical block and them rather than jumping to the front.
const ACTION_RANK = 9000;
const UNKNOWN_RANK = 8000;

const RANK_BY_KEY = new Map();
const META_BY_KEY = new Map();

CANONICAL_COLUMNS.forEach((column, index) => {
  [column.key, ...column.aliases].forEach((key) => {
    RANK_BY_KEY.set(key, index);
    META_BY_KEY.set(key, column);
  });
});

export const isActionColumn = (column) =>
  column?.key === "action" || column?.key === "actions";

/**
 * Display name for a coordinator / team-lead object, falling back the way the
 * reporting boards already do: full name, then email, then nothing. Shared so
 * every table that gained a people column renders it identically.
 */
export const siteVisitPersonName = (person) => {
  if (!person) return "";
  const { firstName, lastName, email, userName } = person;
  if (firstName || lastName) return `${firstName || ""} ${lastName || ""}`.trim();
  return email || userName || "";
};

/** Canonical metadata for whatever spelling a table used, or null. */
export const siteVisitColumnMeta = (key) => META_BY_KEY.get(key) || null;

/** True when `key` is any spelling of `canonicalKey`. */
export const matchesColumn = (key, canonicalKey) =>
  META_BY_KEY.get(key)?.key === canonicalKey;

/** True when the column is one of the three that every table opens with. */
export const isIdentityColumn = (column) =>
  META_BY_KEY.get(column?.key)?.phase === "identity";

/**
 * Reorder a table's columns into the canonical sequence. Stable: columns that
 * share a rank, and unrecognised ones, keep the order the table declared them
 * in. Falsy entries (role-gated `cond && {...}`) are dropped, as antd expects.
 */
export const orderSiteVisitColumns = (columns = []) =>
  columns
    .filter(Boolean)
    .map((column, index) => {
      let rank;
      if (isActionColumn(column)) rank = ACTION_RANK;
      else if (RANK_BY_KEY.has(column.key)) rank = RANK_BY_KEY.get(column.key);
      else rank = UNKNOWN_RANK;
      return { column, rank, index };
    })
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.column);

export default orderSiteVisitColumns;
